import React from 'react';
import { Navigate } from 'react-router-dom';

import { FCC } from '@eluve/components';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { PatientAppointmentsList } from '../PatientAppointmentsList';
import { PatientDetailsPageLayout } from '../PatientDetailsPageLayout';

import { PatientAppointmentsTab } from './PatientAppointmentsTab';
import { PatientProfilePage } from './PatientProfilePage';

// Gates on the sync MEDPLUM flag rather than the async useMedplum().enabled
// so the page never swaps layouts mid-view after Medplum init resolves.
export const useIsPatientProfileEnabled = () => {
  const isNewPatientProfileEnabled = useIsFeatureFlagEnabled(
    'NEW_PATIENT_PROFILE',
  );
  const isMedplumEnabled = useIsFeatureFlagEnabled('MEDPLUM');

  return isNewPatientProfileEnabled && isMedplumEnabled;
};

export const PatientProfileGate: React.FC<{ baseUrl: string }> = ({
  baseUrl,
}) => {
  return useIsPatientProfileEnabled() ? (
    <PatientProfilePage baseUrl={baseUrl} />
  ) : (
    <PatientDetailsPageLayout baseUrl={baseUrl} />
  );
};

export const PatientProfileIndexRedirect: React.FC = () => {
  return useIsPatientProfileEnabled() ? (
    <Navigate to="overview" replace />
  ) : (
    <Navigate to="appointments" replace />
  );
};

/**
 * Sub-routes that exist in only one of the two patient page worlds redirect
 * when the other world is active — by default to the parent's index (which
 * resolves to the active world's default tab), or to `redirectTo` when the
 * content has a direct equivalent in the other world.
 */
export const PatientProfileRouteGuard: FCC<{
  owner: 'profile' | 'legacy';
  redirectTo?: string;
}> = ({ owner, redirectTo = '..', children }) => {
  const isProfileEnabled = useIsPatientProfileEnabled();
  const isOwned = owner === 'profile' ? isProfileEnabled : !isProfileEnabled;

  return isOwned ? children : <Navigate to={redirectTo} replace />;
};

export const PatientProfileAppointmentsRoute: React.FC = () => {
  return useIsPatientProfileEnabled() ? (
    <PatientAppointmentsTab />
  ) : (
    <PatientAppointmentsList />
  );
};
